import { CAPABILITIES } from '@/content/home';
import {
  ACCESS_NOTE,
  CREDENTIAL_TABLE,
  DEFENSE_LAYERS,
  DEPLOY_ITEMS,
  ENDPOINT_TABLE,
  FAQ,
  MODULE_SECTION,
  OVERRIDE_LAYERS,
  PERMISSION_ROWS,
  PLATFORM_INTRO,
  REALTIME_NOTES,
  REALTIME_STEPS,
  STATUS_TABLE,
  THRESHOLD_LEVELS,
  THRESHOLD_NOTE,
} from '@/content/platform';
import { ActionRow, TextLink } from '@/components/ActionButton';
import { Badge } from '@/components/Badge';
import { Callout, Card, FeatureCard } from '@/components/Card';
import { Accordion } from '@/components/Controls';
import { Icon } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { PageHero, Section, SectionHeading } from '@/components/Section';
import { Screenshot } from '@/components/Screenshot';
import { Seo } from '@/components/Seo';
import { SpecTableView } from '@/components/SpecTableView';
import { SHOT_SIZES_FULL } from '@/content/shots';
import { ArchitectureDiagram } from '@/illustrations/ArchitectureDiagram';
import { SingleDroneMock } from '@/illustrations/PlatformUI';

const PERMISSION_TABLE = {
  head: ['角色', '职责范围', '可执行', '不可执行'],
  rows: PERMISSION_ROWS.map((row) => [row.label, row.description, row.grants, row.denied]),
} as const;

const THRESHOLD_TONE: Record<string, 'neutral' | 'solid' | 'outline'> = {
  caution: 'outline',
  warning: 'neutral',
  critical: 'solid',
};

export function PlatformPage() {
  return (
    <>
      <Seo
        path="/platform"
        crumbs={[
          { name: '首页', path: '/' },
          { name: '平台能力', path: '/platform' },
        ]}
      />

      <PageHero
        eyebrow={PLATFORM_INTRO.eyebrow}
        title={PLATFORM_INTRO.title}
        lead={PLATFORM_INTRO.lead}
        meta={
          <>
            <span className="metric-value">1 Hz 遥测</span>
            <span className="metric-value">3 s 断链判定</span>
            <span className="metric-value">180 天审计</span>
            <span className="metric-value">4 类角色</span>
          </>
        }
        actions={<ActionRow actions={PLATFORM_INTRO.actions} />}
        aside={<SingleDroneMock className="w-full max-w-sm" />}
      />

      <Section id="modules">
        <SectionHeading
          eyebrow={MODULE_SECTION.eyebrow}
          title={MODULE_SECTION.title}
          lead={MODULE_SECTION.text}
          actions={<TextLink to="/pricing#trial">申请试用</TextLink>}
        />

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((module, index) => (
            <Reveal key={module.id} delay={index} as="div">
              <div id={module.id}>
                <FeatureCard
                  icon={module.icon}
                  title={module.title}
                  text={module.summary}
                  footer={
                    <ul className="space-y-1.5">
                      {module.points.map((point) => (
                        <li key={point} className="flex gap-2 text-xs leading-relaxed text-fg-muted">
                          <Icon name="check" size={13} className="mt-0.5 text-fg-subtle" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  }
                  className="h-full"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="raised" id="realtime">
        <SectionHeading
          eyebrow="实时链路"
          title="一条遥测链路，从建立到中断的完整过程"
          lead="从探活、推流、降级到判定失联，每一步都有明确状态。链路会中断，界面就必须如实说明。"
        />

        <ol className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {REALTIME_STEPS.map((step, index) => (
            <Reveal key={step.step} delay={index} as="li">
              <Card className="flex h-full flex-col gap-3">
                <div className="flex items-center justify-between">
                  <Icon name={step.icon} size={20} className="text-fg-muted" />
                  <span className="metric-value text-xs text-fg-subtle">{step.step}</span>
                </div>
                <h3 className="text-sm font-semibold text-fg">{step.title}</h3>
                <p className="text-sm leading-relaxed text-fg-muted">{step.text}</p>
              </Card>
            </Reveal>
          ))}
        </ol>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {REALTIME_NOTES.map((note) => (
            <Card key={note.title} className="flex gap-3">
              <Icon name={note.icon} size={18} className="mt-0.5 text-fg-muted" />
              <div>
                <h3 className="text-sm font-semibold text-fg">{note.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-fg-muted">{note.text}</p>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-10">
          <Screenshot id="single-drone-realtime" sizes={SHOT_SIZES_FULL} scrollOnSmall />
        </div>
      </Section>

      <Section id="thresholds">
        <SectionHeading
          eyebrow="告警阈值"
          title="三级阈值，方向不可颠倒"
          lead="注意、警告、严重共用同一套阈值模型，平台默认值、平台设置与单机设置可逐层覆盖。"
        />

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {THRESHOLD_LEVELS.map((level, index) => (
            <Reveal key={level.level} delay={index}>
              <Card className="flex h-full flex-col gap-4">
                <div className="flex items-center justify-between">
                  <Badge tone={THRESHOLD_TONE[level.level] ?? 'neutral'}>{level.label}</Badge>
                  <span className="metric-value text-[0.6875rem] text-fg-subtle">{level.level}</span>
                </div>
                <p className="text-sm leading-relaxed text-fg-muted">{level.text}</p>
                <dl className="mt-auto space-y-2 border-t border-line pt-4">
                  {level.samples.map((sample) => (
                    <div key={sample.name} className="flex items-center justify-between gap-3 text-xs">
                      <dt className="text-fg-muted">{sample.name}</dt>
                      <dd className="metric-value text-fg">{sample.value}</dd>
                    </div>
                  ))}
                </dl>
              </Card>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-start">
          <ol className="flex flex-col gap-3">
            {OVERRIDE_LAYERS.map((layer) => (
              <li key={layer.order} className="flex gap-3 rounded-xl border border-line bg-surface-1 p-4">
                <span className="metric-value text-xs text-fg-subtle">{layer.order}</span>
                <span>
                  <span className="block text-sm font-medium text-fg">{layer.title}</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">{layer.text}</span>
                </span>
              </li>
            ))}
          </ol>
          <Callout label="为什么等级不会同时命中" icon="alert">
            {THRESHOLD_NOTE}
          </Callout>
        </div>

        <div className="mt-10">
          <Screenshot id="alert-thresholds" sizes={SHOT_SIZES_FULL} scrollOnSmall />
        </div>
      </Section>

      <Section tone="soft" id="permissions">
        <SectionHeading
          eyebrow="角色与权限"
          title="四种角色，三层防线"
          lead="前端与后端共用同一份权限定义：界面负责使入口不可达，Worker 负责使操作真正无法执行。"
        />

        <div className="mt-10">
          <SpecTableView table={PERMISSION_TABLE} firstColumnMono={false} />
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {DEFENSE_LAYERS.map((layer) => (
            <Card key={layer.order} className="flex flex-col gap-2">
              <span className="metric-value text-xs text-fg-subtle">{layer.order}</span>
              <h3 className="text-sm font-semibold text-fg">{layer.title}</h3>
              <p className="text-xs leading-relaxed text-fg-muted">{layer.text}</p>
            </Card>
          ))}
        </div>

        <div className="mt-12">
          <h3 className="text-sm font-semibold text-fg">权限之外，还需留下记录</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-fg-muted">
            界面拦截误操作、Worker 拦截越权行为，而真正使权限可追责的是记录本身：登录、令牌签发与指令下发均会留档，
            记录操作人、目标机体、操作类型与时间，默认保留 180 天。
          </p>
          <div className="mt-5">
            <Screenshot id="audit-log" sizes={SHOT_SIZES_FULL} scrollOnSmall />
          </div>
        </div>
      </Section>

      <Section id="access">
        <SectionHeading
          eyebrow="接入协议"
          title="一份接口文档，真机与模拟机通用"
          lead="机体侧只需实现四类接口：探活、遥测推送、指令接收与告警上报。平台不限定实现方式，仅依据 HTTP 状态码判断。"
        />

        <div className="mt-10 grid gap-8">
          <div className="min-w-0">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-fg">
              <Icon name="plug" size={16} className="text-fg-muted" />
              端点字段
            </h3>
            <SpecTableView table={ENDPOINT_TABLE} />
          </div>

          <div className="min-w-0">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-fg">
              <Icon name="key" size={16} className="text-fg-muted" />
              三种凭据模式
            </h3>
            <SpecTableView table={CREDENTIAL_TABLE} />
          </div>

          <div className="min-w-0">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-fg">
              <Icon name="alert" size={16} className="text-fg-muted" />
              状态码到平台反应的映射
            </h3>
            <SpecTableView table={STATUS_TABLE} />
          </div>

          <Callout label="为什么状态码比文案重要" icon="code">
            {ACCESS_NOTE}
          </Callout>
        </div>
      </Section>

      <Section tone="raised" id="deploy">
        <SectionHeading
          eyebrow="部署方式"
          title="三个组成部分，均可独立替换"
          lead="Worker 承载控制面、D1 保存数据、静态前端托管于 Pages。整套既可部署于我们侧，也可部署至贵方自有账号。"
        />

        <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-start">
          <Reveal>
            <ArchitectureDiagram />
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {DEPLOY_ITEMS.map((item) => (
              <Card key={item.title} className="flex flex-col gap-2">
                <Icon name={item.icon} size={18} className="text-fg-muted" />
                <h3 className="text-sm font-semibold text-fg">{item.title}</h3>
                <p className="text-xs leading-relaxed text-fg-muted">{item.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      <Section id="faq">
        <SectionHeading eyebrow="常见问题" title="先澄清最易产生误解之处" />
        <div className="mt-8 max-w-3xl">
          <Accordion items={FAQ} />
        </div>
      </Section>

      <Section tone="soft" spacing="loose">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="label-mono text-brand">下一步</span>
            <h2 className="mt-3 text-2xl font-semibold text-fg md:text-3xl">进入平台体验一次，比阅读十页介绍更直接</h2>
            <p className="mt-3 text-[0.9375rem] leading-relaxed text-fg-muted">
              平台内置虚拟机体机队。无需接入真机，即可完整走一遍注册、总览、单机管理、指令下发与告警流程。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <TextLink to="/pricing#trial">申请试用</TextLink>
            <TextLink to="/solutions">查看落地场景</TextLink>
          </div>
        </div>
      </Section>
    </>
  );
}
